import Axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { URLS } from 'utils/constants/urls';
import { identifiers } from '../../constants/identifiers';
import { fetchFromStorage, removeFromStorage, saveToStorage } from '../../storage';

const baseUrl = process.env.REACT_APP_BASE_API_URL || `${window.location.protocol}//${window.location.hostname}`;
const prefix = process.env.PUBLIC_URL || '/';
const axiosInstance = Axios.create({
  baseURL: baseUrl + prefix,
  headers: { 'Content-Type': 'application/json' },
});
axiosInstance.defaults.timeout = 300000;

let isRefreshing = false;

const refreshSession = () => {
  if (isRefreshing) return;
  const refreshTokenValue = fetchFromStorage(identifiers.refresh_token);
  if (!refreshTokenValue) {
    removeFromStorage(identifiers.access_token);
    return;
  }
  isRefreshing = true;
  const apiRoot = (axiosInstance.defaults.baseURL || `${baseUrl}${prefix}`).replace(/\/+$/, '');

  Axios.post(`${apiRoot}${URLS.keycloakRefreshToken}`, { refreshToken: refreshTokenValue })
    .then(async ({ data, status }) => {
      if (status !== 200) return;

      const accessToken = data.accessToken ?? data.access_token;
      const newRefresh = data.refreshToken ?? data.refresh_token;

      removeFromStorage(identifiers.access_token);
      if (accessToken) saveToStorage(identifiers.access_token, accessToken);
      if (newRefresh) saveToStorage(identifiers.refresh_token, newRefresh);

      try {
        const ctx = await Axios.get(`${apiRoot}${URLS.userContext}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (ctx.status === 200 && ctx.data) {
          const d = ctx.data;
          if (d.privileges !== undefined) saveToStorage(identifiers.privilegeList, d.privileges);
          if (d.previewLink !== undefined) saveToStorage(identifiers.preview, d.previewLink);
          if (d.username !== undefined || d.email !== undefined) {
            const userDetails = JSON.stringify({
              username: d.username ?? d.email,
              email: d.email ?? d.username,
            });
            saveToStorage(identifiers.userDetails, userDetails);
          }
        }
      } catch {
        // Non-fatal: reload will re-run app bootstrap.
      }

      window.location.reload();
    })
    .catch((error: any) => {
      console.log(error);
      removeFromStorage(identifiers.access_token);
      removeFromStorage(identifiers.refresh_token);
      window.location.reload();
    })
    .finally(() => {
      isRefreshing = false;
    });
};

axiosInstance.interceptors.request.use((config: AxiosRequestConfig) => {
  const token = fetchFromStorage(identifiers.access_token);
  const lang = fetchFromStorage(identifiers.selectedLanguage)
    ? JSON.parse(fetchFromStorage(identifiers.selectedLanguage) as any)
    : '';
  const clonedConfig: any = config;

  if (token) {
    clonedConfig.headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }
  clonedConfig.params = {
    lang: lang ? lang.value : 'en_IN',
    ...config.params,
  };
  return clonedConfig;
});

axiosInstance.interceptors.response.use(
  (config: AxiosResponse) => {
    return config;
  },
  (error: any) => {
    const requestUrl = error.response?.config?.url ?? '';
    const isRefreshRequest = requestUrl.includes('refresh-token');
    const isLoginTokenRequest = requestUrl.includes('/api/auth/token');

    if (error.response && error.response.status === 401) {
      if (error.response.data?.error_description === 'Email not verified') {
        return Promise.reject(error);
      }
      if (isRefreshRequest || isLoginTokenRequest) {
        return Promise.reject(error);
      }
      const refreshStored = fetchFromStorage(identifiers.refresh_token);
      if (refreshStored) {
        refreshSession();
      }
      return Promise.reject(error);
    } else if (error.response && error.response.status === 503) {
      window.location.href = '#/errorPage';
    } else {
      return Promise.reject(error);
    }
  },
);

export default axiosInstance;
